"use client";

import { useEffect, useRef } from "react";
import { animate, motion, useInView, useMotionValue, useTransform } from "motion/react";
import type { Dictionary } from "@/i18n/types";

const EXAMPLE_SCORE = 68;
const RADIUS = 88;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
// Arco de 270°, aberto embaixo, como um velocímetro calmo.
const ARC = CIRCUMFERENCE * 0.75;

// A cor do arco é a da faixa da pontuação, igual à legenda.
function colorFor(score: number) {
  if (score < 40) return "#c9694f";
  if (score < 70) return "#d99a3c";
  return "#3f8a67";
}

const ranges = [
  { label: "0–40", color: "bg-score-low" },
  { label: "40–70", color: "bg-score-mid" },
  { label: "70–100", color: "bg-score-high" },
];

/** Visual do passo "Entenda": um exemplo de pontuação, nunca um dado real. */
export function ScoreGauge({ t }: { t: Dictionary["score"] }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -60px 0px" });
  const value = useMotionValue(0);
  const offset = useTransform(value, (v) => ARC - (v / 100) * ARC);
  const rounded = useTransform(value, (v) => Math.round(v));

  useEffect(() => {
    if (!inView) return;
    const controls = animate(value, EXAMPLE_SCORE, { duration: 2.2, ease: [0.22, 1, 0.36, 1] });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <div
      ref={ref}
      className="mx-auto w-full max-w-sm rounded-[2rem] bg-white p-8 shadow-lift"
    >
      <p className="text-center text-sm font-semibold tracking-[0.12em] text-brand-600 uppercase">
        {t.title}
      </p>
      <figure className="relative mx-auto mt-4 size-52">
        <svg viewBox="0 0 200 200" className="size-full rotate-[135deg]" aria-hidden>
          <circle
            cx="100"
            cy="100"
            r={RADIUS}
            fill="none"
            stroke="#e3eaf5"
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={`${ARC} ${CIRCUMFERENCE}`}
          />
          <motion.circle
            cx="100"
            cy="100"
            r={RADIUS}
            fill="none"
            stroke={colorFor(EXAMPLE_SCORE)}
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={`${ARC} ${CIRCUMFERENCE}`}
            style={{ strokeDashoffset: offset }}
          />
        </svg>
        <figcaption className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="sr-only">{t.srLabel.replace("{score}", String(EXAMPLE_SCORE))}</span>
          <motion.span
            aria-hidden
            className="font-display text-6xl font-semibold text-brand-950 tabular-nums"
          >
            {rounded}
          </motion.span>
          <span aria-hidden className="text-base text-ink-soft">
            {t.outOf}
          </span>
        </figcaption>
      </figure>
      <ul className="mt-2 grid grid-cols-3 gap-2 text-center text-sm text-ink-soft">
        {ranges.map((r, i) => (
          <li key={r.label}>
            <span className={`mx-auto mb-1.5 block h-1.5 w-10 rounded-full ${r.color}`} aria-hidden />
            <strong className="block font-semibold text-brand-950">{r.label}</strong>
            {t.ranges[i]}
          </li>
        ))}
      </ul>
      <p className="mt-5 text-center text-sm text-ink-soft">{t.example}</p>
    </div>
  );
}
